import env from '@beam-australia/react-env';
import { toLower, toInteger, isNil, merge } from 'lodash';
import { Base64 } from 'js-base64';

import JSONUtils from '../lib/JSONUtils';

const ENV_CONFIG_DISABLE_DEMO_FEATURES =
  env('DISABLE_DEMO_FEATURES') && env('DISABLE_DEMO_FEATURES') !== ''
    ? `${env('DISABLE_DEMO_FEATURES')}`
    : null;

const ENV_CONFIG_HELP_DESK_LINK =
  env('HELP_DESK_LINK') && env('HELP_DESK_LINK') !== '' ? `${env('HELP_DESK_LINK')}` : null;

const ENV_CONFIG_CONTACT_SUPPORT_LINK =
  env('CONTACT_SUPPORT_LINK') && env('CONTACT_SUPPORT_LINK') !== ''
    ? `${env('CONTACT_SUPPORT_LINK')}`
    : null;

const ENV_CONFIG_RELEASE_NOTES_LINK =
  env('RELEASE_NOTES_LINK') && env('RELEASE_NOTES_LINK') !== ''
    ? `${env('RELEASE_NOTES_LINK')}`
    : null;

const ENV_CONFIG_SENTRY_TRACKER_ID =
  env('SENTRY_TRACKER_ID') && env('SENTRY_TRACKER_ID') !== ''
    ? `${env('SENTRY_TRACKER_ID')}`
    : null;

const ENV_CONFIG_DEPLOY_ENV =
  env('DEPLOY_ENV') && env('DEPLOY_ENV') !== '' ? `${env('DEPLOY_ENV')}` : 'unknown';

const ENV_CONFIG_SENTRY_PERFORMANCE_ALLOWED =
  env('SENTRY_PERFORMANCE_ALLOWED') && env('SENTRY_PERFORMANCE_ALLOWED') !== ''
    ? `${env('SENTRY_PERFORMANCE_ALLOWED')}`
    : null;

const ENV_CONFIG_PH_PJ_ID = env('PH_PJ_ID') && env('PH_PJ_ID') !== '' ? `${env('PH_PJ_ID')}` : null;

const ENV_CONFIG_LOGROCKET_TRACKER_ID =
  env('LR_TRACKER_ID') && env('LR_TRACKER_ID') !== '' ? `${env('LR_TRACKER_ID')}` : null;

const ENV_CONFIG_PENDO_TRACKER_ID =
  env('PENDO_TRACKER_ID') && env('PENDO_TRACKER_ID') !== '' ? `${env('PENDO_TRACKER_ID')}` : null;

const ENV_CONFIG_CORS_URL = env('CORS_URL') ? `${env('CORS_URL')}` : '';

/* Start Temp Params for SSO */
const ENV_CONFIG_AUTO_LOGIN_ENABLED =
  env('AUTO_LOGIN_ENABLED') && env('AUTO_LOGIN_ENABLED') !== ''
    ? `${env('AUTO_LOGIN_ENABLED')}`
    : null;

/* End Temp Params for SSO */

export const HELP_DESK_LINK = ENV_CONFIG_HELP_DESK_LINK;
export const CONTACT_SUPPORT_LINK = ENV_CONFIG_CONTACT_SUPPORT_LINK;
export const RELEASE_NOTES_LINK = ENV_CONFIG_RELEASE_NOTES_LINK;

export const DISABLE_DEMO_FEATURES =
  ENV_CONFIG_DISABLE_DEMO_FEATURES === 'false' || ENV_CONFIG_DISABLE_DEMO_FEATURES === false
    ? false
    : true;

export const SENTRY_TRACKER_ID = ENV_CONFIG_SENTRY_TRACKER_ID;
export const DEPLOY_ENV = ENV_CONFIG_DEPLOY_ENV;
export const SENTRY_PERFORMANCE_ALLOWED = ENV_CONFIG_SENTRY_PERFORMANCE_ALLOWED;
export const PH_PJ_ID = ENV_CONFIG_PH_PJ_ID;
export const LOGROCKET_TRACKER_ID = ENV_CONFIG_LOGROCKET_TRACKER_ID;
export const PENDO_TRACKER_ID = ENV_CONFIG_PENDO_TRACKER_ID;

export const CORS_URL = ENV_CONFIG_CORS_URL;

/* Start Temp Params for SSO */
export const AUTO_LOGIN_ENABLED =
  ENV_CONFIG_AUTO_LOGIN_ENABLED &&
  (ENV_CONFIG_AUTO_LOGIN_ENABLED === 'true' || ENV_CONFIG_AUTO_LOGIN_ENABLED === true)
    ? true
    : false;

/* End Temp Params for SSO */

export const DATA_CHARS_ENABLED = getBoolVar('DATA_CHARS_ENABLED', false);
export const NEXLA_WEBHOOKS_ENABLED = getBoolVar('NEXLA_WEBHOOKS_ENABLED', true);

export const PRODUCT_TOURS_ENABLED = getBoolVar('PRODUCT_TOURS_ENABLED', true);

/* Temp Params for Demo */
export const EXT_CATALOG_ENABLED = getBoolVar('EXT_CATALOG_ENABLED', false);
export const REF_RESOURCES_ENABLED = getBoolVar('REF_RESOURCES_ENABLED', true);
export const CREATE_DATASET_WITH_SCHEMA_ENABLED = getBoolVar(
  'CREATE_DATASET_WITH_SCHEMA_ENABLED',
  true,
);
export const EDIT_DATASET_WITH_SCHEMA_ENABLED = getBoolVar(
  'EDIT_DATASET_WITH_SCHEMA_ENABLED',
  false,
);

export const OUTPUT_SCHEMA_FROM_SOURCE_SAMPLE_ENABLED = getBoolVar(
  'OUTPUT_SCHEMA_FROM_SOURCE_SAMPLE_ENABLED',
  false,
);
export const TEMPLATE_API_TEST_ENABLED = getBoolVar('TEMPLATE_API_TEST_ENABLED', true);

export const BATCH_API_TEST_ENABLED = getBoolVar('BATCH_API_TEST_ENABLED', true);

/* End Temp Params for Demo */
/* Start Temp Params for SSO */
export const GOOGLE_SSO_ENABLED = getBoolVar('GOOGLE_SSO_ENABLED', true);
export const BASIC_LOGIN_ENABLED = getBoolVar('BASIC_LOGIN_ENABLED', true);
/* End Temp Params for SSO */

export const INTUIT_QB_VENDOR_NAME = `${env('QUICKB_APP_NAME')}`;

export const HC_SETTINGS_BY_ORG =
  env('HC_SETTINGS_BY_ORG') && env('HC_SETTINGS_BY_ORG') !== ''
    ? getObjectVar('HC_SETTINGS_BY_ORG', true)
    : {};

export const captchaSettings =
  env('CAPTCHA_SETTINGS') && env('CAPTCHA_SETTINGS') !== ''
    ? getObjectVar('CAPTCHA_SETTINGS', true)
    : {};

export const ORG_FEATURE_FLAG_SETTINGS =
  env('ORG_FEATURE_FLAG_SETTINGS') && env('ORG_FEATURE_FLAG_SETTINGS') !== ''
    ? getObjectVar('ORG_FEATURE_FLAG_SETTINGS', true)
    : { check_limits: false };

export const ORG_CONFIG_OVERRIDES =
  env('ORG_CONFIG_OVERRIDES') && env('ORG_CONFIG_OVERRIDES') !== ''
    ? getObjectVar('ORG_CONFIG_OVERRIDES', true)
    : {};

export const changeIngestionAvailability =
  env('INGESTION_MODE_CONFIG') && env('INGESTION_MODE_CONFIG') !== ''
    ? getObjectVar('INGESTION_MODE_CONFIG', true)
    : {};

export const MAX_UPLOAD_FILE_SIZE =
  env('MAX_UPLOAD_FILE_SIZE') && env('MAX_UPLOAD_FILE_SIZE') !== ''
    ? toInteger(env('MAX_UPLOAD_FILE_SIZE'))
    : null;

export const CLI_DOWNLOAD_LINK = env('CLI_DOWNLOAD_LINK')
  ? `${env('CLI_DOWNLOAD_LINK')}`
  : 'https://cdn.nexla.io/cli/downloads/nexla_cli_latest.zip';

function getBoolVar(name, defaultValue = false, isDynamic = true) {
  const value = isDynamic === false ? name : env(name);

  if (!value || value === '') {
    return defaultValue;
  }

  return value !== '0' && toLower(value) !== 'false';
}

function getObjectVar(name, isEncoded = false, defaultValue = null, isDynamic = true) {
  const value = isDynamic === false ? name : env(name);
  const strParamValue = isEncoded ? Base64.decode(value) : value;
  const paramValue = JSONUtils.parseJson(strParamValue);
  return !isNil(paramValue) ? paramValue : defaultValue;
}

const DEFAULT_WHITE_LABEL_SETTINGS = {
  pagingMode: 'default',
  page: {
    title: 'Nexla',
  },
  brand: {
    logoPortrait: 'https://cdn.nexla.io/ui/assets/brand/v2/nexla-logo-color-portrait.svg',
    logoLandscape: 'https://cdn.nexla.io/ui/assets/brand/v2/logo_nexla_landscape.svg',
    logoMark: 'https://cdn.nexla.io/ui/assets/brand/v2/logo_nexla_mark.svg',
  },
  mainContent: {
    bgColor: '#f5f8f8',
  },
  header: {
    bgColor: 'white',
    navIconColor: '#6154ff',
    avatarBgColor: '#6154ff',
    avatarInitialsColor: '#ffffff',
  },
  sidebar: {
    containerBgColor: '#1b1373',
    submenuBg: '#231c85',
    iconColor: 'white',
    activeIconColor: 'white',
    itemContentColor: 'white',
    activeItemBg: '#6154ff',
    activeItemHoverBg: '#5347e3',
    rootItemHoverBg: '#29208f',
    submenuItemHoverBg: '#1b1373',
    activeMenuItemContentColor: 'white',
    activeCtaItemContentColor: 'white',
  },
  pipelines: {
    bgColor: '#00000008',
  },
  tx: {
    hideGrp: [],
  },
  palette: {
    '--color-primary-50': '#f1f0ff',
    '--color-primary-100': '#e6e4ff',
    '--color-primary-200': '#a49cff',
    '--color-primary-300': '#8e84ff',
    '--color-primary-400': '#776cff',
    '--color-primary-500': '#6154ff',
    '--color-primary-600': '#5347e3',
    '--color-primary-700': '#453ac7',
    '--color-primary-800': '#372dab',
    '--color-primary-900': '#29208f',
    '--color-secondary-50': '#fff5e8',
    '--color-secondary-100': '#ffedd6',
    '--color-secondary-200': '#ffd7ae',
    '--color-secondary-300': '#ffbb86',
    '--color-secondary-400': '#ffa068',
    '--color-secondary-500': '#ff7436',
    '--color-secondary-600': '#db5327',
    '--color-secondary-700': '#b7361b',
    '--color-secondary-800': '#931f11',
    '--color-secondary-900': '#7a0f0a',
  },
};

const ENV_CONFIG_WHITELABEL_SETTINGS = `${process.env?.REACT_APP_WHITELABEL_SETTINGS}`;
const ENV_CONFIG_WHITELABEL_SETTINGS_OBJ =
  ENV_CONFIG_WHITELABEL_SETTINGS &&
  ENV_CONFIG_WHITELABEL_SETTINGS !== '' &&
  ENV_CONFIG_WHITELABEL_SETTINGS !== 'undefined'
    ? getObjectVar(ENV_CONFIG_WHITELABEL_SETTINGS, false, {}, false)
    : {};

/**
 * Overriding the default values based on the whitelabel configuration
 */
export const WHITELABEL_SETTINGS = merge(
  {},
  DEFAULT_WHITE_LABEL_SETTINGS,
  ENV_CONFIG_WHITELABEL_SETTINGS_OBJ,
);

export const SITE_TITLE = process.env.REACT_APP_SITE_TITLE || 'Nexla';

export const TMP_DISABLE_NEXSET_INSPECT = getBoolVar('TMP_DISABLE_NEXSET_INSPECT', false);

export const TMP_ENABLE_MONACO_PRELOAD = getBoolVar('TMP_ENABLE_MONACO_PRELOAD', false);

export const DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE = getBoolVar(
  'DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE',
  true,
);

export const TMP_DISPLAY_FLOW_RUNS = getBoolVar('TMP_DISPLAY_FLOW_RUNS', true);

/* GrowthBook Configuration */
export const GROWTHBOOK_API_HOST =
  env('GROWTHBOOK_API_HOST') && env('GROWTHBOOK_API_HOST') !== ''
    ? `${env('GROWTHBOOK_API_HOST')}`
    : null;
export const GROWTHBOOK_CLIENT_KEY =
  env('GROWTHBOOK_CLIENT_KEY') && env('GROWTHBOOK_CLIENT_KEY') !== ''
    ? `${env('GROWTHBOOK_CLIENT_KEY')}`
    : null;
export const GROWTHBOOK_DECRYPTION_KEY =
  env('GROWTHBOOK_DECRYPTION_KEY') && env('GROWTHBOOK_DECRYPTION_KEY') !== ''
    ? `${env('GROWTHBOOK_DECRYPTION_KEY')}`
    : null;
export const GROWTHBOOK_STREAMING_ENABLED = getBoolVar('GROWTHBOOK_STREAMING', false);
export const GROWTHBOOK_POLLING_ENABLED = getBoolVar('GROWTHBOOK_POLLING', true);
export const GROWTHBOOK_REFRESH_INTERVAL =
  env('GROWTHBOOK_REFRESH_INTERVAL') && env('GROWTHBOOK_REFRESH_INTERVAL') !== ''
    ? toInteger(env('GROWTHBOOK_REFRESH_INTERVAL'))
    : 600000;

/* Embedded Mode Configuration */
// Helper function to parse allowed parent origins from env variable
// Format: comma-separated list of origins, e.g., "https://app.example.com"
function getAllowedParentOrigins() {
  const envValue = env('EMBEDDED_MODE_ALLOWED_ORIGINS');
  if (!envValue || envValue === '') {
    return [];
  }

  const origins = envValue
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);
  return origins;
}

export const isNexlaSubdomain = (origin) => {
  try {
    const url = new URL(origin);
    return url.hostname.endsWith('.nexla.com');
  } catch {
    return false;
  }
};

// By default, no origins are allowed unless explicitly configured via env variable
export const EMBEDDED_MODE_ALLOWED_ORIGINS = getAllowedParentOrigins();

export const isAllowedEmbeddedParentOrigin = (origin) => {
  if (!origin || typeof origin !== 'string') {
    return false;
  }

  return EMBEDDED_MODE_ALLOWED_ORIGINS.includes(origin) || isNexlaSubdomain(origin);
};

/* Polling Configuration */
// Default polling interval in milliseconds (10 seconds)
// Can be overridden via REACT_APP_POLLING_INTERVAL_MS environment variable
// Can also be overridden via GrowthBook feature flag 'ui:polling-config'
export const POLLING_INTERVAL_MS =
  env('POLLING_INTERVAL_MS') && env('POLLING_INTERVAL_MS') !== ''
    ? toInteger(env('POLLING_INTERVAL_MS'))
    : 10000;
